import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import axios from 'axios';
import notify, { MESSAGES } from '../../../utils/notify';
import { confirmAction } from '../../../utils/confirm';

import { api } from '../../../config';
import logger from '../../../utils/logger';
import ActionButton from '../../../Components/Common/ActionButton';
import { FileField, RequiredNote } from '../../../Components/Form';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls'];

const hasAcceptedExtension = fileName =>
  ACCEPTED_EXTENSIONS.some(ext => fileName.toLowerCase().endsWith(ext));

const UploadParcelModal = ({
  isOpen,
  toggle,
  procurementId,
  token,
  onUploaded,
}) => {
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // bersihkan pilihan sebelumnya setiap kali modal dibuka
  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFileError('');
    }
  }, [isOpen]);

  const handleFileChange = event => {
    const selected = event.target.files?.[0] ?? null;

    if (selected && !hasAcceptedExtension(selected.name)) {
      setFile(null);
      setFileError('File must be in .xlsx or .xls format.');
      return;
    }

    setFile(selected);
    setFileError('');
  };

  const handleClose = () => {
    if (isUploading) return;
    toggle();
  };

  const handleUpload = async () => {
    if (!file) {
      setFileError('File is required');
      return;
    }

    if (!token) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    const confirmed = await confirmAction({
      tone: 'warning',
      title: 'Upload parcel file?',
      message:
        'Rows already in the table may be updated or replaced by the contents of this file.',
      subject: file.name,
      confirmLabel: 'Upload file',
      cancelLabel: 'Review again',
    });
    if (!confirmed) return;

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('File', file);
      if (procurementId) {
        formData.append('ProcurementId', procurementId);
      }

      await axios.post(
        `${api.API_URL}/quantity-incoterm-location-detail/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      notify.success('Parcel file uploaded successfully.');
      onUploaded?.();
      toggle();
    } catch (err) {
      // Interceptor axios global menolak dengan string (bahkan undefined),
      // bukan Error — jangan baca .message langsung.
      const status = err?.response?.status;
      logger.error(
        'Error uploading parcel file:',
        status ? `HTTP ${status}` : (err?.message ?? String(err))
      );
      notify.apiError(err, 'upload parcel file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose} className='border-0 pb-0'>
        <span className='fw-semibold'>Upload Freight Request</span>
        <span className='d-block text-muted small fw-normal mt-1'>
          Upload the Parcel Request Freight Availability file that has already
          been filled in.
        </span>
      </ModalHeader>

      <ModalBody>
        <hr className='mt-0' />
        <RequiredNote />
        <FileField
          label='File'
          name='parcelFile'
          required
          accept={ACCEPTED_EXTENSIONS.join(',')}
          hint='Excel file (.xlsx or .xls)'
          error={fileError}
          mode={isUploading ? 'disabled' : 'editable'}
          onChange={handleFileChange}
        />
      </ModalBody>

      <ModalFooter className='border-0'>
        <ActionButton
          variant='secondary'
          icon='ri-close-line'
          onClick={handleClose}
          disabled={isUploading}
        >
          Close
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-upload-2-line'
          onClick={handleUpload}
          loading={isUploading}
        >
          Upload File
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default UploadParcelModal;
